import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  makeStyles,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import DiscountIcon from "@material-ui/icons/LocalOffer";
import BarChartIcon from "@material-ui/icons/BarChart";
import { useSelector } from "react-redux";
import SimpleBackdrop from "../../Components/Loader";

const COLORS = {
  bg: "#F8FAFC",
  mint: "#F0FDF4",
  value: "#065F46",
  label: "#273449",
  border: "#E2E8F0",
  white: "#FFFFFF",
  muted: "#64748B",
  header: "#08784F",
};

  const SALE_DOTS = {
  "Cash Sale": "#10B981",
  "Card Sale": "#3B82F6",
  "Online Sale": "#8B5CF6",
  Receivables: "#F59E0B",
  "Bank Reimbursement": "#EF4444",
};

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    padding: theme.spacing(2),
    backgroundColor: COLORS.bg,
    minHeight: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  baseCard: {
    padding: 0,
    borderRadius: 18,
    height: "100%",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 2px 10px rgba(39, 52, 73, 0.06)",
  },
  salesCard: {
    backgroundColor: COLORS.mint,
    border: `1px solid ${COLORS.border}`,
  },
  discountCard: {
    backgroundColor: "#EFF6FF",
    border: `1px solid ${COLORS.border}`,
  },
  ordersCard: {
    backgroundColor: "#FFF7ED",
    border: `1px solid ${COLORS.border}`,
  },
  cardInner: {
    padding: theme.spacing(2),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  titleIcon: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#D1FAE5",
    color: COLORS.header,
    flexShrink: 0,
  },
  iconPlain: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
  },
  rowItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 4px",
    marginBottom: 0,
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${COLORS.border}`,
    borderRadius: 0,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  softDivider: {
    margin: "12px 0",
    backgroundColor: COLORS.border,
  },
  summaryBox: {
    padding: "14px 14px",
    borderRadius: 14,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    boxShadow: "0 1px 3px rgba(39, 52, 73, 0.04)",
  },
  listBlock: {
    marginTop: 0,
    padding: "6px 12px",
    borderRadius: 14,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    boxShadow: "0 1px 3px rgba(39, 52, 73, 0.04)",
  },
  footerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 13,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  showValue: {
    textDecoration: "underline",
    textDecorationStyle: "solid",
    textUnderlineOffset: 3,
    color: COLORS.value,
    background: "none",
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 8,
    flexShrink: 0,
  },
  salesRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 42px minmax(78px, auto)",
    alignItems: "center",
    columnGap: 6,
    padding: "14px 4px",
    borderBottom: `1px solid ${COLORS.border}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  salesLabelCell: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  salesLabelText: {
    color: COLORS.label,
    fontWeight: 600,
    fontSize: "0.85rem",
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  salesPct: {
    color: COLORS.muted,
    fontSize: "0.8rem",
    fontWeight: 600,
    textAlign: "center",
  },
  salesAmount: {
    color: COLORS.value,
    fontSize: "0.85rem",
    fontWeight: 700,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
}));

const DashboardCards = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLoading = useSelector((state) => state.user.isFetching);
  const [showMonthSales, setShowMonthSales] = useState(false);
  // const [showAvgDay, setShowAvgDay] = useState(false);

  const toggleMonthSales = () => setShowMonthSales(!showMonthSales);

  // Discounts breakdown
  const discBreakdown = [
    { label: "Restaurant", orders: data?.totalOtherDisc || 0 },
    { label: "Owner", orders: data?.totalOwnerDisc || 0 },
    { label: "Bank", orders: data?.totalBankDisc || 0 },
    { label: "Complimentary", orders: data?.totalComplimentary || 0 },
  ];

  //Calculate total Discount
  const totalDiscount =
    (data?.totalOtherDisc || 0) +
    (data?.totalOwnerDisc || 0) +
    (data?.totalComplimentary || 0) +
    (data?.totalBankDisc || 0);

  // Render Discount breakdown in the specified order
  const renderDiscBreakdown = () => {
    return discBreakdown.map(({ label, orders }) => (
      <Box key={label} className={classes.rowItem}>
        <Typography
          variant="body2"
          style={{
            color: COLORS.label,
            textTransform: "capitalize",
            fontWeight: 600,
            fontSize: isMobile ? "0.9rem" : "0.85rem",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          style={{
            color: COLORS.value,
            fontSize: isMobile ? "0.9rem" : "0.85rem",
            fontWeight: 700,
          }}
        >
          {Math.round(orders).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      </Box>
    ));
  };

  // Orders breakdown
  const ordersBreakdown = [
    { label: "Food Panda", orders: data?.totalFPOrders || 0 },
    { label: "Dastak", orders: data?.totalDastakOrders || 0 },
    { label: "Take-Aways", orders: data?.totalTAOrders || 0 },
    { label: "Deliveries", orders: data?.totalDelivOrders || 0 },
    { label: "Dine-in", orders: data?.totalDineInOrders || 0 },
  ];

  // Render orders breakdown in the specified order
  const renderOrdersBreakdown = () => {
    return ordersBreakdown.map(({ label, orders }) => (
      <Box key={label} className={classes.rowItem}>
        <Typography
          variant="body2"
          style={{
            color: COLORS.label,
            textTransform: "capitalize",
            fontWeight: 600,
            fontSize: isMobile ? "0.9rem" : "0.85rem",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          style={{
            color: COLORS.value,
            fontSize: isMobile ? "0.9rem" : "0.85rem",
            fontWeight: 700,
          }}
        >
          {orders}
        </Typography>
      </Box>
    ));
  };

  // Sales breakdown
  const tCashSale = data?.totalCashSale || 0;
  const tCreditCardSale = data?.totalCrSale || 0;
  const tOnlineSale = data?.totalOnlineSale || 0;
  const tRecievSales = data?.totalReceivables || 0;
  const tCompSales = data?.totalComplimentary || 0;
  const tBankReimb = data?.totalBankDiscReImbur || 0;

  const totalSale = (data?.totalSale || 0) + tBankReimb;
  const salesBreakdownTotal =
    tCashSale + tCreditCardSale + tOnlineSale + tRecievSales + tBankReimb;

  const tSaleExclgst = (data?.totalSale || 0) - (data?.totalGST || 0);

  //Calculate Average/Cover
  const avgCover = (totalSale) / (data?.totalNoOfCover || 0);
  // console.log(avgCover)

  //Calculate Average per day
  const avgPerDay = (data?.gTotalSale || 0) / (data?.totalNoOfDays || 0);
  // console.log(avgPerDay)

  // Render sales breakdown with separate entries for each category
  const renderSalesBreakdown = () => {
    const salesItems = [
      { label: "Cash Sale", amount: tCashSale },
      { label: "Card Sale", amount: tCreditCardSale },
      { label: "Online Sale", amount: tOnlineSale },
      { label: "Receivables", amount: tRecievSales },
      // { label: "Complimentary", amount: tCompSales },
      { label: "Bank Reimbursement", amount: tBankReimb },
    ];

    return salesItems.map(({ label, amount }) => (
      <Box key={label} className={classes.salesRow}>
        <Box className={classes.salesLabelCell}>
          <span
            className={classes.colorDot}
            style={{ backgroundColor: SALE_DOTS[label] || COLORS.header }}
          />
          <Typography variant="body2" className={classes.salesLabelText} title={label}>
            {label}
          </Typography>
        </Box>
        <Typography variant="body2" className={classes.salesPct}>
          {Math.round((amount / salesBreakdownTotal) * 100) || 0}%
        </Typography>
        <Typography variant="body2" className={classes.salesAmount}>
          {Math.round(amount).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      </Box>
    ));
  };

  if (isLoading) {
    return <SimpleBackdrop />;
  }

  return (
    <Box className={classes.pageWrap}>
      <Grid container spacing={2}>
        {/* Sales Card  */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} className={`${classes.baseCard} ${classes.salesCard}`}>
            <Box className={classes.cardInner}>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                marginBottom: 16,
                gap: 12,
              }}
            >
              {/* Left: Total Sales */}
              <Box>
                <Box className={classes.titleRow}>
                  <Box className={classes.titleIcon}>
                    <BarChartIcon style={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    style={{
                      color: COLORS.label,
                      fontWeight: 600,
                      fontSize: isMobile ? "1rem" : "0.95rem",
                      letterSpacing: "0.01em",
                      margin: 0,
                    }}
                  >
                    Total Sales
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  style={{
                    color: COLORS.value,
                    fontWeight: 700,
                    fontSize: isMobile ? "1.55rem" : "2rem",
                  }}
                >
                  {Math.round(totalSale).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              {/* Right: Month Sales and Avg. Per Day */}
              <Box
                className={classes.summaryBox}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minWidth: isMobile ? 130 : 150,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    style={{
                      color: COLORS.label,
                      fontWeight: 600,
                      fontSize: isMobile ? "0.85rem" : "0.85rem",
                      lineHeight: 1.2,
                    }}
                  >
                    Month Sales
                  </Typography>
                  <Typography
                    variant="h6"
                    className={!showMonthSales ? classes.showValue : undefined}
                    style={{
                      color: COLORS.value,
                      fontWeight: 700,
                      fontSize: isMobile ? "0.9rem" : "1.05rem",
                      minWidth: "110px",
                      display: "inline-block",
                      cursor: "pointer",
                      lineHeight: 1.25,
                    }}
                    onClick={toggleMonthSales}
                  >
                    {showMonthSales
                      ? `${Math.round(data?.gTotalSale || 0).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "Show Value"}
                  </Typography>
                </Box>
                <Divider className={classes.softDivider} style={{ margin: "6px 0" }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    style={{
                      color: COLORS.label,
                      fontWeight: 600,
                      fontSize: isMobile ? "0.85rem" : "0.85rem",
                      lineHeight: 1.2,
                    }}
                  >
                    Avg. / Day
                  </Typography>
                  <Typography
                    variant="h6"
                    className={!showMonthSales ? classes.showValue : undefined}
                    style={{
                      color: COLORS.value,
                      fontWeight: 700,
                      fontSize: isMobile ? "0.9rem" : "1.05rem",
                      minWidth: "110px",
                      display: "inline-block",
                      cursor: "pointer",
                      lineHeight: 1.25,
                    }}
                    onClick={toggleMonthSales}
                  >
                    {showMonthSales
                      ? `${Math.round(avgPerDay).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : "Show Value"}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className={classes.listBlock} style={{ marginTop: 16 }}>
              {renderSalesBreakdown()}
            </Box>
            {/* Sales Summary */}
            <Box className={classes.summaryBox} style={{ marginTop: 16 }}>
              <Box className={classes.footerRow}>
                <Typography
                  variant="body2"
                  style={{ color: COLORS.label, fontWeight: 600 }}
                >
                  GST Amount
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: COLORS.value,
                    fontSize: isMobile ? "0.95rem" : "0.875rem",
                    fontWeight: 700,
                  }}
                >
                  {Math.round(data?.totalGST || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Box className={classes.footerRow}>
                <Typography
                  variant="body2"
                  style={{ color: COLORS.label, fontWeight: 600 }}
                >
                  Amount Excl. GST
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: COLORS.value,
                    fontSize: isMobile ? "0.95rem" : "0.875rem",
                    fontWeight: 700,
                  }}
                >
                  {Math.round(tSaleExclgst).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
              <Divider className={classes.softDivider} style={{ margin: "12px 0" }} />
              <Box className={classes.footerRow}>
                <Typography
                  variant="body2"
                  style={{ color: COLORS.label, fontWeight: 600 }}
                >
                  Total No. of covers
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: COLORS.value,
                    fontSize: isMobile ? "0.95rem" : "0.875rem",
                    fontWeight: 700,
                  }}
                >
                  {data?.totalNoOfCover || 0}
                </Typography>
              </Box>
              <Box className={classes.footerRow}>
                <Typography
                  variant="body2"
                  style={{ color: COLORS.label, fontWeight: 600 }}
                >
                  Avg. / cover
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: COLORS.value,
                    fontSize: isMobile ? "0.95rem" : "0.875rem",
                    fontWeight: 700,
                  }}
                >
                  {Math.round(avgCover || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Discount Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} className={`${classes.baseCard} ${classes.discountCard}`}>
            <Box className={classes.cardInner}>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  style={{
                    color: COLORS.label,
                    marginBottom: 8,
                    fontWeight: 600,
                    fontSize: isMobile ? "1.05rem" : "0.95rem",
                    letterSpacing: "0.01em",
                  }}
                >
                  Discounts
                </Typography>
                <Typography
                  variant="h4"
                  style={{
                    color: COLORS.value,
                    fontWeight: 700,
                    fontSize: isMobile ? "1.55rem" : "2rem",
                  }}
                >
                  {Math.round(totalDiscount || 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                </Typography>
              </Box>
              <Box className={classes.iconPlain} style={{ backgroundColor: "#DBEAFE" }}>
                <DiscountIcon
                  style={{
                    color: "#2563EB",
                    fontSize: isMobile ? "1.35rem" : "1.5rem",
                  }}
                />
              </Box>
            </Box>
            <Divider className={classes.softDivider} />
            <Box className={classes.listBlock}>{renderDiscBreakdown()}</Box>
            </Box>
          </Paper>
        </Grid>

        {/* Orders Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} className={`${classes.baseCard} ${classes.ordersCard}`}>
            <Box className={classes.cardInner}>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  style={{
                    color: COLORS.label,
                    marginBottom: 8,
                    fontWeight: 600,
                    fontSize: isMobile ? "1.05rem" : "0.95rem",
                    letterSpacing: "0.01em",
                  }}
                >
                  Total Orders
                </Typography>
                <Typography
                  variant="h4"
                  style={{
                    color: COLORS.value,
                    fontWeight: 700,
                    fontSize: isMobile ? "1.55rem" : "2rem",
                  }}
                >
                  {data?.totalOrders || 0}
                </Typography>
              </Box>
              <Box className={classes.iconPlain} style={{ backgroundColor: "#FFEDD5" }}>
                <ShoppingCartIcon
                  style={{
                    color: "#EA580C",
                    fontSize: isMobile ? "1.35rem" : "1.5rem",
                  }}
                />
              </Box>
            </Box>
            <Divider className={classes.softDivider} />
            <Box className={classes.listBlock}>{renderOrdersBreakdown()}</Box>
            <Divider className={classes.softDivider} />
            <Box className={classes.rowItem}>
              <Typography
                variant="body2"
                style={{
                  color: COLORS.label,
                  textTransform: "capitalize",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.9rem" : "0.85rem",
                }}
              >
                Cancelled Orders
              </Typography>
              <Typography
                variant="body2"
                style={{
                  color: COLORS.value,
                  fontSize: isMobile ? "0.9rem" : "0.85rem",
                  fontWeight: 700,
                }}
              >
                {data?.totalCancOrders || 0}
              </Typography>
            </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardCards;
